import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LogIn, LogOut, Trash2, Download, Users, MessageSquare, Eye, X, GraduationCap, Ticket, Plus, Video, ClipboardCheck } from "lucide-react";
import wtaLogo from "@/assets/wta-logo.png";
import LessonVideosSection from "@/components/admin/LessonVideosSection";
import HomeworkReviewSection from "@/components/admin/HomeworkReviewSection";

interface Lead {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  source: string | null;
  created_at: string;
}

interface Inquiry {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  message: string;
  created_at: string;
}

interface Enrollment {
  id: string;
  email: string;
  product_id: string;
  amount_cents: number;
  status: string;
  scholarship_code: string | null;
  created_at: string;
}

interface ScholarshipCode {
  id: string;
  code: string;
  product_id: string;
  max_redemptions: number;
  redemption_count: number;
  expires_at: string | null;
  active: boolean;
  created_at: string;
}

const Admin = () => {
  const [session, setSession] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [codes, setCodes] = useState<ScholarshipCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [accessError, setAccessError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"lessons" | "homework" | "leads" | "inquiries" | "enrollments" | "codes">("lessons");
  const [viewingInquiry, setViewingInquiry] = useState<Inquiry | null>(null);
  const [newCode, setNewCode] = useState({ code: "", max: 1, expires: "" });
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        if (event === "PASSWORD_RECOVERY") setRecoveryMode(true);
      }
    );
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) {
      loadAdminData();
    } else {
      setIsAdmin(false);
      setLeads([]);
      setInquiries([]);
      setAccessError("");
    }
  }, [session]);

  const loadAdminData = async () => {
    setDataLoading(true);
    setAccessError("");

    const admin = await checkAdminRole();
    setIsAdmin(admin);

    if (!admin) {
      setLeads([]);
      setInquiries([]);
      setAccessError("This account does not have admin access.");
      setDataLoading(false);
      return;
    }

    await Promise.all([fetchLeads(), fetchInquiries(), fetchEnrollments(), fetchCodes()]);
    setDataLoading(false);
  };

  const checkAdminRole = async () => {
    if (!session?.user?.id) return false;

    const { data: existingRole, error: roleError } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", session.user.id)
      .eq("role", "admin")
      .maybeSingle();

    if (roleError) {
      toast.error("Failed to verify admin access");
      return false;
    }

    if (existingRole) return true;

    await supabase.functions.invoke("assign-admin-role");

    const { data: assignedRole } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", session.user.id)
      .eq("role", "admin")
      .maybeSingle();

    return !!assignedRole;
  };

  const fetchLeads = async () => {
    const { data, error } = await supabase
      .from("leads")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error("Failed to load leads");
    else setLeads(data || []);
  };

  const fetchInquiries = async () => {
    const { data, error } = await supabase
      .from("inquiries")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error("Failed to load inquiries");
    else setInquiries((data as Inquiry[]) || []);
  };

  const fetchEnrollments = async () => {
    const { data, error } = await supabase
      .from("enrollments")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error("Failed to load enrollments");
    else setEnrollments((data as Enrollment[]) || []);
  };

  const fetchCodes = async () => {
    const { data, error } = await supabase
      .from("scholarship_codes")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error("Failed to load scholarship codes");
    else setCodes((data as ScholarshipCode[]) || []);
  };

  const handleCreateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCode.code) return;
    const { error } = await supabase.from("scholarship_codes").insert({
      code: newCode.code.trim().toUpperCase(),
      product_id: "wta_academy_enrollment",
      max_redemptions: newCode.max || 1,
      expires_at: newCode.expires ? new Date(newCode.expires).toISOString() : null,
      created_by: session?.user?.id,
    });
    if (error) return toast.error(error.message);
    toast.success("Scholarship code created");
    setNewCode({ code: "", max: 1, expires: "" });
    fetchCodes();
  };

  const toggleCode = async (id: string, active: boolean) => {
    const { error } = await supabase.from("scholarship_codes").update({ active: !active }).eq("id", id);
    if (error) return toast.error(error.message);
    fetchCodes();
  };

  const deleteCode = async (id: string) => {
    const { error } = await supabase.from("scholarship_codes").delete().eq("id", id);
    if (error) return toast.error(error.message);
    setCodes((prev) => prev.filter((c) => c.id !== id));
    toast.success("Code deleted");
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) toast.error(error.message);
    setAuthLoading(false);
  };

  const handleResetPassword = async () => {
    if (!email) {
      toast.error("Enter your email above first, then tap Reset Password.");
      return;
    }
    setAuthLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/admin`,
    });
    setAuthLoading(false);
    if (error) toast.error(error.message);
    else toast.success(`Password reset link sent to ${email}.`);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setIsAdmin(false);
    setLeads([]);
    setInquiries([]);
    setAccessError("");
  };

  const handleDeleteLead = async (id: string) => {
    const { error } = await supabase.from("leads").delete().eq("id", id);
    if (error) toast.error("Failed to delete");
    else {
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead removed");
    }
  };

  const handleDeleteInquiry = async (id: string) => {
    const { error } = await supabase.from("inquiries").delete().eq("id", id);
    if (error) toast.error("Failed to delete");
    else {
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      setViewingInquiry(null);
      toast.success("Inquiry removed");
    }
  };

  const handleExportLeads = () => {
    const csv = [
      "Name,Email,Phone,Source,Date Signed Up",
      ...leads.map((l) => `"${l.name || ""}","${l.email}","${l.phone || ""}","${l.source || ""}","${new Date(l.created_at).toLocaleDateString()}"`),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wta-leads-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportInquiries = () => {
    const csv = [
      "Name,Email,Phone,Message,Date",
      ...inquiries.map((i) =>
        `"${i.name}","${i.email}","${i.phone || ""}","${i.message.replace(/"/g, '""')}","${new Date(i.created_at).toLocaleDateString()}"`
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wta-inquiries-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  if (loading || dataLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="space-y-4 text-center">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
          {session && <p className="font-body text-sm text-muted-foreground">Loading admin portal...</p>}
        </div>
      </div>
    );
  }

  if (recoveryMode) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="w-full max-w-sm space-y-8">
          <div className="text-center space-y-4">
            <img src={wtaLogo} alt="WTA Logo" className="w-20 h-20 mx-auto object-contain" />
            <h1 className="font-display text-2xl font-bold text-foreground">Set a New Password</h1>
            <p className="text-muted-foreground font-body text-sm">Choose a password you'll remember.</p>
          </div>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (newPassword.length < 8) {
                toast.error("Password must be at least 8 characters.");
                return;
              }
              setAuthLoading(true);
              const { error } = await supabase.auth.updateUser({ password: newPassword });
              setAuthLoading(false);
              if (error) toast.error(error.message);
              else {
                toast.success("Password updated. You're signed in.");
                setRecoveryMode(false);
                setNewPassword("");
              }
            }}
            className="space-y-4"
          >
            <input
              type="password"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 8 characters)"
              className="w-full px-4 py-3 bg-secondary/50 border border-gold/20 text-foreground placeholder:text-muted-foreground font-body text-sm focus:outline-none focus:border-gold/60"
            />
            <button
              type="submit"
              disabled={authLoading}
              className="w-full py-3 bg-gradient-gold text-primary-foreground font-body text-sm font-semibold uppercase tracking-[0.2em] disabled:opacity-50"
            >
              {authLoading ? "Updating..." : "Update Password"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm space-y-8"
        >
          <div className="text-center space-y-4">
            <img src={wtaLogo} alt="WTA Logo" className="w-20 h-20 mx-auto object-contain" />
            <h1 className="font-display text-2xl font-bold text-foreground">Admin Portal</h1>
            <p className="text-muted-foreground font-body text-sm">Sign in to manage your leads</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
              className="w-full px-4 py-3 bg-secondary/50 border border-gold/20 text-foreground placeholder:text-muted-foreground font-body text-sm focus:outline-none focus:border-gold/60 transition-colors" />
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
              className="w-full px-4 py-3 bg-secondary/50 border border-gold/20 text-foreground placeholder:text-muted-foreground font-body text-sm focus:outline-none focus:border-gold/60 transition-colors" />
            <motion.button type="submit" disabled={authLoading} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              className="w-full py-3 bg-gradient-gold text-primary-foreground font-body text-sm font-semibold uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-50">
              <LogIn size={16} />
              {authLoading ? "Signing in..." : "Sign In"}
            </motion.button>
            <button
              type="button"
              onClick={handleResetPassword}
              disabled={authLoading}
              className="w-full text-center text-xs text-gold hover:text-gold/80 font-body uppercase tracking-[0.2em] disabled:opacity-50"
            >
              Forgot password? Send reset link
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  if (accessError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="w-full max-w-sm text-center space-y-6">
          <img src={wtaLogo} alt="WTA Logo" className="w-20 h-20 mx-auto object-contain" />
          <div className="space-y-2">
            <h1 className="font-display text-2xl font-bold text-foreground">Admin Portal</h1>
            <p className="text-muted-foreground font-body text-sm">{accessError}</p>
          </div>
          <button onClick={handleLogout}
            className="inline-flex items-center gap-2 px-5 py-2.5 border border-gold/20 text-gold font-body text-sm uppercase tracking-[0.15em] hover:bg-gold/5 transition-colors">
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-gold/10 bg-background/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={wtaLogo} alt="WTA" className="w-8 h-8 object-contain" />
            <span className="font-display text-lg font-semibold text-gradient-gold">Admin</span>
          </div>
          <button onClick={handleLogout}
            className="flex items-center gap-2 text-muted-foreground hover:text-gold font-body text-sm transition-colors">
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-12 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="p-6 border border-gold/15 bg-card space-y-2">
            <Users className="text-gold" size={20} />
            <p className="text-3xl font-display font-bold text-foreground">{leads.length}</p>
            <p className="text-muted-foreground font-body text-sm">Total Signups</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="p-6 border border-gold/15 bg-card space-y-2">
            <Users className="text-gold" size={20} />
            <p className="text-3xl font-display font-bold text-foreground">
              {leads.filter((l) => {
                const d = new Date(l.created_at); const now = new Date();
                return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
              }).length}
            </p>
            <p className="text-muted-foreground font-body text-sm">Signups This Month</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="p-6 border border-gold/15 bg-card space-y-2">
            <MessageSquare className="text-gold" size={20} />
            <p className="text-3xl font-display font-bold text-foreground">{inquiries.length}</p>
            <p className="text-muted-foreground font-body text-sm">Total Inquiries</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="p-6 border border-gold/15 bg-card space-y-2">
            <MessageSquare className="text-gold" size={20} />
            <p className="text-3xl font-display font-bold text-foreground">
              {inquiries.filter((i) => {
                const d = new Date(i.created_at); const now = new Date();
                const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                return d >= weekAgo;
              }).length}
            </p>
            <p className="text-muted-foreground font-body text-sm">Inquiries This Week</p>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-6 border-b border-gold/10 overflow-x-auto">
          <button
            onClick={() => setActiveTab("lessons")}
            className={`pb-3 font-body text-sm uppercase tracking-[0.2em] font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === "lessons" ? "text-gold border-gold" : "text-muted-foreground border-transparent hover:text-foreground"
            }`}
          >
            <span className="flex items-center gap-2"><Video size={16} /> Lessons (13)</span>
          </button>
          <button
            onClick={() => setActiveTab("homework")}
            className={`pb-3 font-body text-sm uppercase tracking-[0.2em] font-medium transition-colors border-b-2 whitespace-nowrap ${
              activeTab === "homework" ? "text-gold border-gold" : "text-muted-foreground border-transparent hover:text-foreground"
            }`}
          >
            <span className="flex items-center gap-2"><ClipboardCheck size={16} /> Homework</span>
          </button>
          <button
            onClick={() => setActiveTab("leads")}
            className={`pb-3 font-body text-sm uppercase tracking-[0.2em] font-medium transition-colors border-b-2 ${
              activeTab === "leads" ? "text-gold border-gold" : "text-muted-foreground border-transparent hover:text-foreground"
            }`}
          >
            <span className="flex items-center gap-2"><Users size={16} /> Leads ({leads.length})</span>
          </button>
          <button
            onClick={() => setActiveTab("inquiries")}
            className={`pb-3 font-body text-sm uppercase tracking-[0.2em] font-medium transition-colors border-b-2 ${
              activeTab === "inquiries" ? "text-gold border-gold" : "text-muted-foreground border-transparent hover:text-foreground"
            }`}
          >
            <span className="flex items-center gap-2"><MessageSquare size={16} /> Inquiries ({inquiries.length})</span>
          </button>
          <button
            onClick={() => setActiveTab("enrollments")}
            className={`pb-3 font-body text-sm uppercase tracking-[0.2em] font-medium transition-colors border-b-2 ${
              activeTab === "enrollments" ? "text-gold border-gold" : "text-muted-foreground border-transparent hover:text-foreground"
            }`}
          >
            <span className="flex items-center gap-2"><GraduationCap size={16} /> Enrollments ({enrollments.length})</span>
          </button>
          <button
            onClick={() => setActiveTab("codes")}
            className={`pb-3 font-body text-sm uppercase tracking-[0.2em] font-medium transition-colors border-b-2 ${
              activeTab === "codes" ? "text-gold border-gold" : "text-muted-foreground border-transparent hover:text-foreground"
            }`}
          >
            <span className="flex items-center gap-2"><Ticket size={16} /> Scholarship Codes ({codes.length})</span>
          </button>
        </div>

        {/* Actions */}
        {(activeTab === "leads" || activeTab === "inquiries") && (
          <div className="flex justify-end">
            <motion.button
              onClick={activeTab === "leads" ? handleExportLeads : handleExportInquiries}
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-5 py-2.5 border border-gold/20 text-gold font-body text-sm uppercase tracking-[0.15em] hover:bg-gold/5 transition-colors"
            >
              <Download size={16} /> Export CSV
            </motion.button>
          </div>
        )}

        {/* Lesson Videos */}
        {activeTab === "lessons" && <LessonVideosSection />}

        {/* Homework Review */}
        {activeTab === "homework" && <HomeworkReviewSection />}

        {/* Enrollments Table */}
        {activeTab === "enrollments" && (() => {
          const leadByEmail = new Map(leads.map((l) => [l.email.toLowerCase(), l]));
          return (
          <div className="border border-gold/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gold/10 bg-secondary/30">
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Name</th>
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Email</th>
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Phone</th>
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Product</th>
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Amount</th>
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Status</th>
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollments.map((en) => {
                    const lead = leadByEmail.get(en.email.toLowerCase());
                    return (
                    <tr key={en.id} className="border-b border-gold/5 hover:bg-secondary/20 transition-colors">
                      <td className="px-6 py-4 font-body text-sm text-foreground">{lead?.name || "—"}</td>
                      <td className="px-6 py-4 font-body text-sm text-foreground">{en.email}</td>
                      <td className="px-6 py-4 font-body text-sm text-muted-foreground">{lead?.phone || "—"}</td>
                      <td className="px-6 py-4 font-body text-sm text-muted-foreground">{en.product_id}</td>
                      <td className="px-6 py-4 font-body text-sm text-foreground">${(en.amount_cents / 100).toFixed(2)}</td>
                      <td className="px-6 py-4 font-body text-xs uppercase tracking-wider">
                        <span className={en.status === "scholarship" ? "text-gold" : "text-foreground"}>{en.status}</span>
                        {en.scholarship_code && <span className="ml-2 text-muted-foreground">({en.scholarship_code})</span>}
                      </td>
                      <td className="px-6 py-4 font-body text-sm text-muted-foreground">{formatDate(en.created_at)}</td>
                    </tr>
                    );
                  })}
                  {enrollments.length === 0 && (
                    <tr><td colSpan={7} className="px-6 py-12 text-center text-muted-foreground font-body text-sm">No enrollments yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          );
        })()}

        {/* Scholarship Codes */}
        {activeTab === "codes" && (
          <div className="space-y-6">
            <form onSubmit={handleCreateCode} className="border border-gold/15 bg-card p-6 grid gap-4 md:grid-cols-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground font-body mb-2">Code</label>
                <input
                  value={newCode.code}
                  onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                  placeholder="CHURCH2026"
                  className="w-full px-3 py-2 bg-secondary/50 border border-gold/20 text-foreground font-body text-sm uppercase tracking-widest focus:outline-none focus:border-gold/60"
                  required
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground font-body mb-2">Max uses</label>
                <input
                  type="number"
                  min={1}
                  value={newCode.max}
                  onChange={(e) => setNewCode({ ...newCode, max: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 bg-secondary/50 border border-gold/20 text-foreground font-body text-sm focus:outline-none focus:border-gold/60"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground font-body mb-2">Expires (optional)</label>
                <input
                  type="date"
                  value={newCode.expires}
                  onChange={(e) => setNewCode({ ...newCode, expires: e.target.value })}
                  className="w-full px-3 py-2 bg-secondary/50 border border-gold/20 text-foreground font-body text-sm focus:outline-none focus:border-gold/60"
                />
              </div>
              <div className="flex items-end">
                <button type="submit" className="w-full py-2.5 bg-gradient-gold text-primary-foreground font-body text-xs font-semibold uppercase tracking-[0.15em] flex items-center justify-center gap-2">
                  <Plus size={14} /> Create
                </button>
              </div>
            </form>

            <div className="border border-gold/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gold/10 bg-secondary/30">
                      <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Code</th>
                      <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Used</th>
                      <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Expires</th>
                      <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Status</th>
                      <th className="text-right px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {codes.map((c) => (
                      <tr key={c.id} className="border-b border-gold/5 hover:bg-secondary/20 transition-colors">
                        <td className="px-6 py-4 font-body text-sm text-foreground uppercase tracking-widest">{c.code}</td>
                        <td className="px-6 py-4 font-body text-sm text-muted-foreground">{c.redemption_count} / {c.max_redemptions}</td>
                        <td className="px-6 py-4 font-body text-sm text-muted-foreground">{c.expires_at ? formatDate(c.expires_at) : "—"}</td>
                        <td className="px-6 py-4 font-body text-xs uppercase tracking-wider">
                          <span className={c.active ? "text-gold" : "text-muted-foreground"}>{c.active ? "Active" : "Disabled"}</span>
                        </td>
                        <td className="px-6 py-4 text-right space-x-3">
                          <button onClick={() => toggleCode(c.id, c.active)} className="text-muted-foreground hover:text-gold font-body text-xs uppercase tracking-wider">
                            {c.active ? "Disable" : "Enable"}
                          </button>
                          <button onClick={() => deleteCode(c.id)} className="text-muted-foreground hover:text-destructive">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {codes.length === 0 && (
                      <tr><td colSpan={5} className="px-6 py-12 text-center text-muted-foreground font-body text-sm">No scholarship codes yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Leads Table */}
        {activeTab === "leads" && (
          <div className="border border-gold/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gold/10 bg-secondary/30">
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Name</th>
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Email</th>
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Phone</th>
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Source</th>
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Date</th>
                    <th className="text-right px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, i) => (
                    <motion.tr key={lead.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                      className="border-b border-gold/5 hover:bg-secondary/20 transition-colors">
                      <td className="px-6 py-4 font-body text-sm text-foreground">{lead.name || "—"}</td>
                      <td className="px-6 py-4 font-body text-sm text-foreground">{lead.email}</td>
                      <td className="px-6 py-4 font-body text-sm text-muted-foreground">{lead.phone || "—"}</td>
                      <td className="px-6 py-4 font-body text-sm text-muted-foreground">{lead.source || "—"}</td>
                      <td className="px-6 py-4 font-body text-sm text-muted-foreground">{formatDate(lead.created_at)}</td>
                      <td className="px-6 py-4 text-right">
                        {isAdmin && (
                          <button onClick={() => handleDeleteLead(lead.id)} className="text-muted-foreground hover:text-destructive transition-colors" title="Remove lead">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                  {leads.length === 0 && (
                    <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground font-body text-sm">No signups yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Inquiries Table */}
        {activeTab === "inquiries" && (
          <div className="border border-gold/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gold/10 bg-secondary/30">
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Name</th>
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Email</th>
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium hidden md:table-cell">Phone</th>
                    <th className="text-left px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Date</th>
                    <th className="text-right px-6 py-4 font-body text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {inquiries.map((inq, i) => (
                    <motion.tr key={inq.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                      className="border-b border-gold/5 hover:bg-secondary/20 transition-colors">
                      <td className="px-6 py-4 font-body text-sm text-foreground">{inq.name}</td>
                      <td className="px-6 py-4 font-body text-sm text-foreground">{inq.email}</td>
                      <td className="px-6 py-4 font-body text-sm text-muted-foreground hidden md:table-cell">{inq.phone || "—"}</td>
                      <td className="px-6 py-4 font-body text-sm text-muted-foreground">{formatDate(inq.created_at)}</td>
                      <td className="px-6 py-4 text-right space-x-3">
                        <button onClick={() => setViewingInquiry(inq)} className="text-muted-foreground hover:text-gold transition-colors" title="View message">
                          <Eye size={16} />
                        </button>
                        {isAdmin && (
                          <button onClick={() => handleDeleteInquiry(inq.id)} className="text-muted-foreground hover:text-destructive transition-colors" title="Delete inquiry">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                  {inquiries.length === 0 && (
                    <tr><td colSpan={5} className="px-6 py-12 text-center text-muted-foreground font-body text-sm">No inquiries yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Inquiry Detail Modal */}
      {viewingInquiry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm px-6" onClick={() => setViewingInquiry(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg bg-card border border-gold/15 p-8 space-y-5 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setViewingInquiry(null)} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors">
              <X size={20} />
            </button>
            <h3 className="font-display text-xl font-bold text-foreground">Session Inquiry</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-body mb-1">Name</p>
                <p className="font-body text-sm text-foreground">{viewingInquiry.name}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-body mb-1">Email</p>
                <p className="font-body text-sm text-foreground">{viewingInquiry.email}</p>
              </div>
              {viewingInquiry.phone && (
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-body mb-1">Phone</p>
                  <p className="font-body text-sm text-foreground">{viewingInquiry.phone}</p>
                </div>
              )}
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-body mb-1">Message</p>
                <p className="font-body text-sm text-foreground leading-relaxed whitespace-pre-wrap">{viewingInquiry.message}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-body mb-1">Received</p>
                <p className="font-body text-sm text-foreground">{formatDate(viewingInquiry.created_at)}</p>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <a href={`mailto:${viewingInquiry.email}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-gold text-primary-foreground font-body text-sm uppercase tracking-[0.15em] font-semibold hover:opacity-90 transition-opacity">
                Reply via Email
              </a>
              {isAdmin && (
                <button onClick={() => handleDeleteInquiry(viewingInquiry.id)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-destructive/30 text-destructive font-body text-sm uppercase tracking-[0.15em] hover:bg-destructive/10 transition-colors">
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Admin;
